import { FormBuilder } from '@/components/formBuilder';
import { useEmailField } from '@/components/formBuilder/formFields/useEmailField';
import { fetchSelectOptions } from '@/components/formBuilder/helpers/formHelpers';
import { useAuth, useShowToast } from '@/hooks';
import { sanitizeDom } from '@/lib/purify';
import {
  Flex,
  Icon,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Box,
} from '@chakra-ui/react';
import { PlusCircle } from '@kula-ai/icons/dual-tone/PlusCircle';
import { SearchMd } from '@kula-ai/icons/outline/SearchMd';
import { useEffect, useRef, useState, useMemo } from 'react';
import { Edit03 } from '@kula-ai/icons/outline/Edit03';
import { CenterSpinner } from '../loadders';
import { KText } from '@/ui-library/components/text';
import { useKFlags } from '@/hooks/use-k-flags';
import { EmailTemplateDrawer } from './EmailTemplateDrawer';
import { ScheduleForLater } from '../job-stage-actions/actions/assessment/send-assessment/schedule/ScheduleForLater';
import { KAutoComplete } from '@/ui-library/components/organisms/KFields/KAutoComplete';
import { Formik } from 'formik';
import { components } from 'react-select';
import { LayoutLeft } from '@kula-ai/icons/dual-tone/LayoutLeft';
import { useGetTemplatesVariablesQuery } from '@/store/query/email-templates-settings';
import { getTemplatesCategoryConfig } from '@/pages/settings/ats/templates-settings/constants';

export const CustomValueContainer = ({ children, ...props }) => (
  <components.ValueContainer {...props}>
    <Flex alignItems="center" gap="4px">
      <Icon as={LayoutLeft} color="secondary-700" />
      <Box>{children}</Box>
    </Flex>
  </components.ValueContainer>
);

function _processHtmlString(htmlString) {
  // Create a temporary DOM element to manipulate the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;

  // Find all <span> elements within the temporary div
  const spans = tempDiv.querySelectorAll('span');

  spans.forEach((span) => {
    // Get the computed style of the span
    const color = span.style.color;

    // Create a new font tag with the same color and text
    const newFont = document.createElement('font');
    newFont.color = color;
    newFont.innerHTML = span.innerHTML;

    // Replace the original span with the new font tag in the parent
    const parent = span.parentElement;
    const existingFont = parent.querySelector('font[color]');

    if (existingFont) {
      // If there's an existing font, insert the new font before it
      parent.insertBefore(newFont, existingFont);
    } else {
      // If no existing font, replace the span with the new font
      parent.replaceChild(newFont, span);
    }
  });

  // Return the modified HTML string
  return tempDiv.innerHTML;
}

export const EmailTemplates = ({
  initialValues = {},
  modalConfig = {},
  channel = 'email',
  category = [
    'candidate_adhoc',
    'candidate_application_rejected',
    'candidate_offer_notice',
    'candidate_assessment_notice',
    'candidate_job_stage_action_notice',
  ],
  formNameKey = '',
  formik: parentFormik = {},
  emailFormSubmit = undefined,
  renderOnMount = false,
  mode = 'choose', //choose, edit, create
  label = undefined,
  subLabel = undefined,
  modalActions = {},
  actionLabel = {
    after: `Choose another template`,
    before: `Choose template`,
  },
  jobId = undefined,
  disabled = false,
  isModalCtaLoading = false,
  skipTemplateSelection = false,
  userEmailType = 'users', // users or 'hiringTeam
  nonRemovableAttachments = [],
  onAttachmentRemove = undefined,
  defaultAllowedFromUsers = undefined,
  isReplyMode = false,
  scheduleRef = null,
  selected = [],
  unSelected: _unSelected = [],
  isDrawer = false,
  isScheduleEmail = false,
  isEditMode = false,
  isScheduled = false,
  setIsScheduled = (_data) => {},
  templatesCategoryViews = [],
  skippedCandidates = [],
  recipients = [],
}) => {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState();
  const [isCtaLoading, setIsCtaLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: customIsOpen,
    onOpen: customOnOpen = () => {},
    onClose: customOnClose = () => {},
  } = modalActions;
  const toast = useShowToast();
  const [selectedTemplate, setSelectedTemplate] = useState({});
  const userInputRef = useRef();
  const createFormRef = useRef();
  const { feSharedEmails, feAdhocEmailScheduling, feSurvey, feCandidateAvailability } =
    useKFlags();

  const templatesCategoryConfig = getTemplatesCategoryConfig({
    feSurvey,
    feCandidateAvailability,
  });

  const view = [
    ...templatesCategoryConfig.candidate_adhoc.view,
    ...(Array.isArray(templatesCategoryViews) ? templatesCategoryViews : []),
  ];
  const { data: { variables = [] } = {} } = useGetTemplatesVariablesQuery({
    params: { view },
  });

  const variableList = skipTemplateSelection ? undefined : variables;

  const { currUser } = useAuth();
  const emailFieldConfigs = useEmailField({
    formRef: createFormRef,
    userEmailType,
    jobId,
    templateData: selectedTemplate,
    defaultAllowedFromUsers,
    isReplyMode,
    variables: variableList,
    onAttachmentRemove,
  });

  const getButtonText = () => {
    if (isScheduleEmail) {
      if (!isScheduled) {
        return 'Send email';
      } else {
        return 'Save this email';
      }
    } else if (mode === 'create') {
      if (renderOnMount) {
        return 'Send email';
      } else {
        return 'Save this email';
      }
    } else {
      return 'Use this template';
    }
  };

  // Create a stable endpoint that doesn't change
  const buildEndPoint = useMemo(() => {
    if (typeof category === 'string') {
      return `/ats_comm_templates?filter_by[channel]=${channel}&filter_by[category][]=${category}&items=200&filter_by[name]=`;
    }

    if (Array.isArray(category)) {
      const categories = category
        .map((cat) => `&filter_by[category][]=${cat}`)
        .join('');
      return `/ats_comm_templates?filter_by[channel]=${channel}${categories}&items=200&filter_by[name]=`;
    }

    throw new Error(
      'Invalid category type: must be a string or an array of strings.'
    );
  }, [category, channel]);

  // Single data fetching operation with strong safeguards
  const fetchTemplates = async () => {
    try {
      setIsLoading(true);

      const apiConfig = {
        endPoint: buildEndPoint,
      };

      const list = await fetchSelectOptions(apiConfig);
      setTemplates(list || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Setup initial data fetch
  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    modalTitle = 'Choose email template',
    modalDescription = 'Select an auto-reply email template to use with candidates',
  } = modalConfig;

  const handleModalSubmit = async () => {
    const currentValues = parentFormik?.values;
    if (mode === 'choose') {
      const newValues = {
        ...currentValues,
        [formNameKey]: selectedTemplate?.id,
      };
      parentFormik?.setValues(newValues);
      onClose();
      customOnClose();
    } else if (mode === 'create' && createFormRef?.current) {
      const form = createFormRef?.current;
      const { errors } = form || {};

      // Check for any form validation errors
      const [firstErrorField, errorMessage] =
        Object.entries(errors || {})[0] || [];
      if (firstErrorField) {
        toast({
          title: errorMessage,
          status: 'error',
        });
        return;
      }

      // Submit the form
      form.handleSubmit();
    }
  };

  const renderLeftContent = (template = {}, index) => {
    const { id, name } = template;
    return (
      <Flex
        key={id}
        w="100%"
        cursor="pointer"
        onClick={() => setSelectedTemplate(template)}
        flexDir="column"
        borderTop={index === 0 && mode === 'create' && '1px solid'}
        borderBottom="1px solid"
        borderColor="border-color"
        p="1em"
        gap=".5em"
        bgColor={id === selectedTemplate?.id ? 'ats-tertiary' : undefined}
      >
        <Flex justifyContent="space-between" w="100%">
          <KText size="md-semi-bold">{name}</KText>
        </Flex>
      </Flex>
    );
  };

  const renderRightContent = () => {
    const {
      id: _id,
      name: _name,
      subject,
      body,
      attachments: _attachments,
    } = selectedTemplate || {};

    const handleFormSubmit = (values = {}) => {
      const { body, from, subject } = values;
      if (selectedTemplate?.id) {
        values.selectedTemplate = selectedTemplate;
      }
      if (!from) {
        toast({ title: `From is mandatory`, status: 'error' });
        return;
      } else if (!subject && !isReplyMode) {
        toast({ title: `Subject is mandatory`, status: 'error' });
        return;
      } else if (!body) {
        toast({ title: `Body is mandatory`, status: 'error' });
        return;
      } else if (renderOnMount) {
        emailFormSubmit?.(
          values,
          // { ...values, from, subject, body: processHtmlString(body) },
          () => {
            onClose();
            customOnClose();
          },
          setIsCtaLoading
        );
      } else {
        const currentValues = parentFormik?.values;

        const newValues = {
          ...currentValues,
          ...values,
          // from,
          // subject,
          // body: processHtmlString(body),
          [formNameKey]: selectedTemplate?.id,
        };
        parentFormik?.setValues(newValues);
        onClose();
        customOnClose();
      }
    };

    const emailFormConfig = [
      {
        id: 'emailFormConfig',
        type: 'wrapper',
        fields: emailFieldConfigs,
        Wrapper: Box,
        wrapperProps: {
          border: '1px solid',
          borderColor: 'border-color',
          borderRadius: 'sm',
        },
      },
    ];

    if (mode === 'create') {
      let ccEmails = initialValues?.cc?.map((cc) => cc?.email);
      let bccEmails = initialValues?.bcc?.map((bcc) => bcc?.email);
      const sender =
        createFormRef?.current?.values?.fromUserObject || initialValues?.sender;
      const fromEmail = sender?.email;

      if (!fromEmail) {
        ccEmails = ccEmails?.filter((cc) => cc !== currUser?.email);
        bccEmails = bccEmails?.filter((bcc) => bcc !== currUser?.email);
      }

      // for sharedEmails we need to assign whole user objects
      const fromUserObject =
        sender && typeof sender === 'object'
          ? sender
          : {
              id: currUser?.id,
              email: currUser?.email,
              name: `${currUser?.first_name ?? ''} ${
                currUser?.last_name ?? ''
              }`.trim(),
              avatar: currUser?.avatar,
            };

      // Get existing form values if form ref exists
      const existingValues = createFormRef?.current?.values || {};
      const selectedFromUserObject =
        existingValues?.fromUserObject || fromUserObject;
      return (
        <Flex
          p="1em"
          w="100%"
          flexDir="column"
          className="offer-email-container"
          data-test-id="offer-email-container"
        >
          <FormBuilder
            initialValues={{
              from: fromEmail || currUser?.email,
              cc: ccEmails || [],
              bcc: bccEmails || [],
              subject:
                selectedTemplate?.subject || parentFormik?.values?.subject,
              body: selectedTemplate?.body || parentFormik?.values?.body,
              attachments: [
                ...nonRemovableAttachments,
                ...(selectedTemplate?.attachments ||
                  parentFormik?.values?.attachments ||
                  []),
              ],
              ...(feSharedEmails
                ? { fromUserObject: selectedFromUserObject }
                : {}),
              ...(isEditMode ? { ...initialValues } : {}),
            }}
            config={emailFormConfig}
            onSubmit={handleFormSubmit}
            formRef={createFormRef}
          />
        </Flex>
      );
    }

    return (
      <Flex
        border="1px solid"
        borderColor="border-color"
        borderRadius="sm"
        flexDir="column"
        m="1em"
        w="100%"
      >
        <Flex
          p=".75em"
          gap=".5em"
          borderBottom="1px solid"
          borderColor="border-color"
        >
          <KText size="sm-semi-bold">From</KText>
          <KText size="sm-medium">{`{{your_email_address}}`}</KText>
        </Flex>
        <Flex
          p=".75em"
          gap=".5em"
          borderBottom="1px solid"
          borderColor="border-color"
        >
          <KText size="sm-semi-bold">Subject</KText>
          <KText size="sm-medium">{subject}</KText>
        </Flex>
        <Flex p=".75em" gap=".5em">
          <KText
            dangerouslySetInnerHTML={{ __html: sanitizeDom(body) }}
          ></KText>
        </Flex>
      </Flex>
    );
  };

  const filteredData = userInputRef?.current?.value
    ? templates?.filter((i) => i?.name?.includes(userInputRef?.current?.value))
    : templates;

  useEffect(() => {
    setSelectedTemplate(filteredData?.[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInputRef?.current?.value]);

  const selectOnChange = (value, _item) => {
    setSelectedTemplate(value);
    if (createFormRef.current && isEditMode) {
      createFormRef.current.setValues({
        ...createFormRef.current.values,
        subject: value?.subject || createFormRef.current.values.subject,
        body: value?.body || createFormRef.current.values.body,
        attachments: [
          ...nonRemovableAttachments,
          ...(value?.attachments || []),
        ],
      });
    }
  };

  const renderTemplateSelection = () => {
    return mode === 'create' ? (
      <Formik initialValues={{}} onSubmit={() => {}}>
        <KAutoComplete
          item={{
            name: 'emailTemplates',
            label: 'Email Templates',
            type: 'autoComplete',
            useInput: true,
            isRequired: true,
            isMulti: false,
            placeholder: `Search templates`,
            labelKey: 'name',
            valueKey: 'id',
            customOnChange: selectOnChange,
            options: templates,
            updateEndpointOnChange: true,
            fetchConfig: {
              method: 'get',
              endPoint: buildEndPoint,
              processResponse: (res) => {
                const options = res?.data?.map((item) => ({
                  label: item?.name,
                  value: item?.id,
                  ...item,
                }));

                return options;
              },
            },
            customStyles: {
              container: (provided) => ({
                ...provided,
                width: '50%',
                height: 'fit-content',
              }),
            },
            components: {
              ValueContainer: CustomValueContainer,
            },
          }}
        />
      </Formik>
    ) : (
      <InputGroup w="fit-content">
        <InputLeftElement
          pointerEvents="none"
          children={
            <Icon as={SearchMd} color="secondary-800" width="18" h="100%" />
          }
        />
        <Input
          placeholder={'Search'}
          ref={userInputRef}
          w="250px"
          _placeholder={{
            opacity: 1,
            fontSize: '12px',
            fontWeight: 'medium',
            color: 'secondary-400',
          }}
        />
      </InputGroup>
    );
  };

  const renderHeader = () => {
    return (
      <Flex
        flexDir="column"
        p={`1.5em 1.5em ${mode === 'create' ? '0.5em' : '1em'} 1.5em`}
        w="100%"
        justifyContent="space-between"
      >
        <Flex gap="3rem" justifyContent="space-between" w="100%">
          <Flex flexDir="column" gap=".5rem">
            <KText size="md-medium" color="secondary-900">
              {modalTitle}
            </KText>
            <KText fontSize="sm" fontWeight="400">
              {modalDescription}
            </KText>
          </Flex>
          {!skipTemplateSelection && renderTemplateSelection()}
        </Flex>
      </Flex>
    );
  };

  const renderBody = () => {
    if (filteredData?.length === 0 && mode === 'choose')
      return (
        <Flex
          width="100%"
          alignItems="center"
          justifyContent="center"
          minH="50vh"
        >
          <KText size="xl-medium">No templates found</KText>
        </Flex>
      );

    return (
      <>
        <Flex
          w="100%"
          borderTop="1px solid"
          borderColor="border-color"
          minH="50vh"
        >
          {mode !== 'create' && (
            <Flex
              flexDir="column"
              borderRight="1px solid"
              borderColor="border-color"
              w="35%"
            >
              <Flex w="100%" flexDir="column" maxH="300px" overflowY="auto">
                {filteredData?.map(renderLeftContent)}
              </Flex>
            </Flex>
          )}
          <Flex
            w={mode !== 'create' ? '75%' : '100%'}
            overflowY={mode === 'choose' && 'auto'}
          >
            {renderRightContent()}
          </Flex>
        </Flex>
        {isScheduled && isScheduleEmail && (
          <ScheduleForLater
            scheduleRef={scheduleRef}
            scheduleDetails={selected}
            onSchedule={() => {
              scheduleRef.current.submitForm();
              setIsScheduled(true);
            }}
            onCancelSchedule={() => {
              handleCancelScheduleAction();
            }}
            isScheduling={isModalCtaLoading || isCtaLoading}
            isScheduled={true}
          />
        )}
      </>
    );
  };

  const renderModal = () => {
    return (
      <Modal
        isOpen={isOpen || customIsOpen}
        onClose={() => {
          onClose();
          customOnClose();
        }}
        maxH="85vh"
      >
        <ModalOverlay />
        <ModalContent minW={mode !== 'create' ? '70%' : '55%'} pos="fixed">
          {renderHeader()}
          <ModalBody p="0" minH="50vh" maxH="70vh" overflowY="auto">
            {isLoading ? <CenterSpinner h="50vh" /> : renderBody()}
          </ModalBody>
          <ModalFooter
            gap="6px"
            borderTop="1px solid"
            borderColor="border-color"
          >
            <Button
              variant="secondary"
              onClick={() => {
                onClose();
                customOnClose();
              }}
              data-test-id="secondary-cta-button"
            >
              Cancel
            </Button>
            {isScheduleEmail && !isScheduled && feAdhocEmailScheduling && (
              <ScheduleForLater
                scheduleRef={scheduleRef}
                scheduleDetails={selected}
                onSchedule={() => {
                  scheduleRef.current.submitForm();
                  handleModalSubmit();
                  setIsScheduled(true);
                }}
                onCancelSchedule={handleCancelScheduleAction}
                isScheduling={isModalCtaLoading || isCtaLoading}
              />
            )}
            <Button
              onClick={handleModalSubmit}
              variant="primary"
              disabled={
                (!selectedTemplate?.id && mode !== 'create') ||
                isModalCtaLoading ||
                isCtaLoading ||
                (skippedCandidates?.length > 0 && recipients?.length === 0)
              }
              isLoading={isModalCtaLoading || isCtaLoading}
              data-test-id="primary-cta-button"
            >
              {getButtonText()}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };

  const isValueSelected =
    mode === 'choose'
      ? parentFormik?.values[formNameKey]
      : mode === 'create' && parentFormik?.values?.body;

  const renderAction = () => {
    return (
      <Flex gap=".5em" flexDir="column" w="100%">
        {label && <KText size="sm-semi-bold">{label}</KText>}
        {subLabel && <KText fontSize="sm">{subLabel}</KText>}
        {isValueSelected && (
          <Flex gap="15px" alignItems="center">
            <KText
              w="100%"
              p="1em"
              mb="10px"
              border="1px solid"
              borderColor="border-color"
              size="sm-medium"
              borderRadius="sm"
              bgColor="secondary-50"
            >
              {mode === 'create'
                ? `${parentFormik?.values?.subject}`
                : parentFormik?.values[formNameKey]?.name ||
                  selectedTemplate?.name}
            </KText>
            {mode === 'choose' && !disabled && (
              <Icon
                as={Edit03}
                onClick={() => {
                  onOpen();
                  customOnOpen();
                }}
                cursor="pointer"
                _hover={{
                  color: 'ats-primary',
                }}
                mt="-10px"
              />
            )}
          </Flex>
        )}
        {!isValueSelected && (
          <Flex
            alignItems="center"
            gap=".5em"
            pb="1em"
            opacity={disabled ? '0.5' : '1'}
            onClick={() => {
              if (!disabled) {
                onOpen();
                customOnOpen();
              }
            }}
            cursor={disabled ? 'not-allowed' : 'pointer'}
          >
            <Icon as={PlusCircle} color="ats-primary" />
            <KText color="primary-500">
              {actionLabel?.before}
              {/* {isValueSelected ? actionLabel?.after : actionLabel?.before} */}
            </KText>
          </Flex>
        )}
      </Flex>
    );
  };

  const handleCancelScheduleAction = () => {
    setIsScheduled(false);
  };

  return (
    <Flex>
      {!renderOnMount && renderAction()}
      {isDrawer && feAdhocEmailScheduling ? (
        <EmailTemplateDrawer
          isOpen={isOpen}
          customIsOpen={customIsOpen}
          onClose={onClose}
          customOnClose={customOnClose}
          mode={mode}
          handleModalSubmit={handleModalSubmit}
          isLoading={isLoading}
          isModalCtaLoading={isModalCtaLoading}
          renderOnMount={renderOnMount}
          modalTitle={modalTitle}
          modalDescription={modalDescription}
          skipTemplateSelection={skipTemplateSelection}
          templates={templates}
          selectOnChange={selectOnChange}
          renderLeftContent={renderLeftContent}
          renderRightContent={renderRightContent}
          scheduleRef={scheduleRef}
          scheduleDetails={selected}
          onSchedule={() => {
            scheduleRef.current.submitForm();
            if (!isEditMode || !isScheduled) {
              handleModalSubmit();
            }
            setIsScheduled(true);
          }}
          onCancelSchedule={handleCancelScheduleAction}
          isScheduling={isLoading}
          isScheduleEmail={isScheduleEmail}
          endpointString={buildEndPoint}
          isScheduled={isScheduled}
          setIsScheduled={setIsScheduled}
          skippedCandidates={skippedCandidates}
          recipients={recipients}
        />
      ) : (
        renderModal()
      )}
    </Flex>
  );
};
